// NOTE:
// Do not use TailwindCSS classes in this molecule. Please define the styles in
// the `error-page.scss` file or through neeto-ui helper classes.
// The error.js entrypoint does not use TailwidCSS.

import { useEffect } from "react";

import DOMPurify from "dompurify";
import { useErrorDisplayStore } from "neetocommons/react-utils/useDisplayErrorPage";
import { Button, Typography } from "neetoui";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { Trans, useTranslation } from "react-i18next";

import { ERRORS } from "./constants";
import "./error-page.scss";

const sanitize = DOMPurify.sanitize;

const ErrorPage = ({
  homeUrl = "/",
  status,
  backToHomeButtonProps,
  children,
  showNeetoChatWidget = true,
  description,
  title,
  hideBackToHomeButton = false,
}) => {
  const { t } = useTranslation();
  const { statusCode: storeStatusCode, showErrorPage } =
    useErrorDisplayStore.pick();

  status = status || storeStatusCode;
  const { Image, errorKey, titleKey } = ERRORS[status] || ERRORS[404];

  useEffect(() => {
    const pageLoader = document.getElementById("neeto-page-loader");
    if (pageLoader) pageLoader.remove();

    const onBackButtonPress = () => {
      if (!showErrorPage) return;
      useErrorDisplayStore.setState({ showErrorPage: false });
    };

    window.addEventListener("popstate", onBackButtonPress);

    return () => {
      window.removeEventListener("popstate", onBackButtonPress);
    };
  }, []);

  const openChatWidget = () => {
    window.NeetoChat?.contextualHelp?.maximizeWidget();
    window.NeetoChat?.contextualHelp?.openWidget();
  };

  return (
    <div
      className="neeto-molecules-error-page__wrapper"
      data-testid="error-page-container"
    >
      <div className="neeto-molecules-error-page__container">
        <Image
          className="neeto-molecules-error-code neeto-molecules-error-page__image"
          data-testid="error-image"
        />
        <div className="neeto-ui-flex neeto-ui-flex-col justify-center md:min-w-0 md:flex-grow">
          <Helmet>
            <title>{t(titleKey)}</title>
          </Helmet>
          <Typography
            className="neeto-ui-mb-4"
            data-testid="title"
            style="h2"
            weight="semibold"
          >
            {title || <Trans i18nKey={errorKey} />}
          </Typography>
          {!!description && (
            <Typography
              className="neeto-ui-mb-4"
              dangerouslySetInnerHTML={{ __html: sanitize(description) }}
              data-testid="description"
              style="body1"
            />
          )}
          {showNeetoChatWidget && (
            <Typography
              className="neeto-ui-mb-6"
              data-testid="title"
              style="body1"
            >
              <Trans
                i18nKey="neetoMolecules.errorPage.contactUs"
                components={{
                  button: (
                    <Button
                      className="neeto-ui-font-size-inherit"
                      size="large"
                      style="link"
                      onClick={openChatWidget}
                    />
                  ),
                }}
              />
            </Typography>
          )}
          <div className="neeto-molecules-error-page__action-btn">
            {!hideBackToHomeButton && (
              <Button
                href={homeUrl}
                label={t("neetoMolecules.errorPage.backToHome")}
                size="large"
                style="primary"
                {...backToHomeButtonProps}
              />
            )}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

ErrorPage.propTypes = {
  /**
   * The URL to which the user will be taken to when user clicks on back to home button.
   */
  homeUrl: PropTypes.string,
  /**
   * The status code of the error.
   */
  status: PropTypes.oneOf([403, 404, 500]),
  /**
   * Decides whether 'contact us' link should be displayed or not.
   */
  showNeetoChatWidget: PropTypes.bool,
  /**
   * Description about the error.
   */
  description: PropTypes.string,
  /**
   * Decides whether 'back to home' button should be displayed or not.
   */
  hideBackToHomeButton: PropTypes.bool,
  /**
   * The title of the error page.
   */
  title: PropTypes.string,
};

export default ErrorPage;
